import React, { useCallback, useEffect, useMemo, useState } from "react";
import { observer } from "mobx-react-lite";

import PageLayout from "../_layout/PageLayout";
import {
  ManageDeletedData,
  ViewLinkedSewerObjects,
  ViewLinkedSewerObjectsFromAssignedProject,
} from "../../services/authorization/Permissions";
import DataGrid from "../../components/datatable/DataGrid";
import useUrlParams from "../../services/url/UrlParamsService";
import { LinkedObjectsStorageModel } from "../../services/sewer-object/LinkedObjectsStorage";
import { api } from "../../services/api/ApiService";
import {
  ActionsSection,
  ButtonsSection,
} from "../../components/data-components/sections/DataSections";
import DeleteButton from "../../components/buttons/delete-button/DeleteButton";
import { BadRequestError } from "../../services/api/BadRequestError";
import useErrorHandler, {
  ApplicationError,
} from "../../services/error-handler/ErrorHandlerService";
import { CurrentNodeStorageModel } from "../../services/sewer-object/CurrentNodeStorage";
import { NodeMenuItemsModel } from "./node-details/NodeMenuItems";
import { permissionsService } from "../../services/authorization/UserPermissionsService";
import { useHistory } from "react-router-dom";
import useRouteService from "../../services/route-service/RouteService";
import { EntityTypeFromPath } from "../../services/breadcrumbs/EntityTypeFromPathModel";
import { breadcrumbsStorage } from "../../services/breadcrumbs/BreadcrumbsStorage";

export default observer(() => {
  const history = useHistory();
  const urlParamsService = useUrlParams();
  const { handleErrorInSnackbar, handleErrorInModal } = useErrorHandler();
  const { isUserOnProjects } = useRouteService();

  const nodeId = useMemo(() => urlParamsService.getNodeId()!, [urlParamsService]);

  const [currentNodeStorage] = useState(() =>
    CurrentNodeStorageModel.create({
      nodeId: nodeId,
      parentEntityFromPath: {
        id: urlParamsService.getSewerObjectParentId()!,
        type: isUserOnProjects() ? EntityTypeFromPath.Project : EntityTypeFromPath.Job
      }
    }, { api })
  );

  const [linkedObjectsStorage] = useState(() =>
    LinkedObjectsStorageModel.create(
      {
        sewerObjectId: nodeId,
        useGridFiltering: true,
      },
      { api }
    )
  );

  const menuItems = useMemo(() => {
    const nodeMenuItems = NodeMenuItemsModel.create({
      nodeId: currentNodeStorage.nodeId,
      parentIdFromPath: currentNodeStorage.parentEntityFromPath.id
    });

    return {
      staticItems: nodeMenuItems.getStaticMenuItems(),
    };
  }, [currentNodeStorage.nodeId, currentNodeStorage.parentEntityFromPath]);

  const deleteSelectedSewerObjects = useCallback(async () => {
    linkedObjectsStorage.setIsDeletingSewerObjects(true);
    try {
      await linkedObjectsStorage.deleteSewerObjects(linkedObjectsStorage.selectedItems);
    } catch (error) {
      const applicationError = error as ApplicationError;

      if (applicationError as BadRequestError) {
        applicationError.withModalTitleKey("general.sewer-objects.delete-error.title");
        handleErrorInModal(applicationError);
      } else {
        applicationError.withErrorMessage("default-error.sewer-objects.delete-from-project");
        handleErrorInSnackbar(applicationError);
      }
    }
    linkedObjectsStorage.setIsDeletingSewerObjects(false);
  }, [handleErrorInModal, handleErrorInSnackbar, linkedObjectsStorage]);

  useEffect(() => {
    async function loadBreadcrumbsData() {
      breadcrumbsStorage.setEntityNames([{
      entity: currentNodeStorage.parentEntityFromPath.type,
      name: await currentNodeStorage.getParentEntityName(currentNodeStorage.currentNode.project.name)
      }, {
      entity: EntityTypeFromPath.SewerObject,
      name: currentNodeStorage.currentNode.name
      }
    ]);

    breadcrumbsStorage.updateBreadcrumbs(history.location.pathname);
    }

    loadBreadcrumbsData();
  }, [currentNodeStorage, currentNodeStorage.currentNode, history.location.pathname]);

  return (
    <PageLayout
      accessWithOneOfThePermissions={[ViewLinkedSewerObjects, ViewLinkedSewerObjectsFromAssignedProject]}
      titleKey="ViewNodeLinkedObjects.title"
      titleContext={{ nodeName: currentNodeStorage.currentNode.name }}
      id="view-node-linked-objects"
      menuItems={menuItems}
      isLoading={currentNodeStorage.isLoading}
    >
      <ActionsSection>
        <ButtonsSection>
          <DeleteButton
            disabled={linkedObjectsStorage.isDeleteDisabled}
            confirmationAction={deleteSelectedSewerObjects}
            confirmationModalTitleKey="ProjectSewerObjects.delete.confirmation.title"
            confirmationModalTitleContext={{
              sewerObjectsCount: linkedObjectsStorage.selectedItemsCount,
            }}
            confirmationModalSubtitleKey="ProjectSewerObjects.delete.confirmation"
            canUserRestoreDeletedEntity={permissionsService.userHasPermissionTo(ManageDeletedData)}
          />
        </ButtonsSection>
      </ActionsSection>
      <DataGrid
        itemTypeKey="general.linked-objects"
        dataGridStorage={linkedObjectsStorage}
        noResultsMessageKey="general.list.no-linked-objects"
      />
    </PageLayout>
  );
});
